/* ===========================================================
   shared/blocks.js — helpers over ProseMirror JSON

   A document's plain-data form ({ type, attrs, content, text, marks })
   is what history, compare, merge and search work on. These helpers
   are shared by the app, the Worker and the tests, so nothing here
   touches the DOM.
   =========================================================== */

/** A stable string for a node: same content gives the same string,
    whatever the key order or whether empty attrs/marks were written. */
export function canon(node) {
  return JSON.stringify(normal(node));
}

function normal(v) {
  if (Array.isArray(v)) return v.map(normal);
  if (!v || typeof v !== 'object') return v;
  const out = {};
  for (const k of Object.keys(v).sort()) {
    const x = v[k];
    if (x === null || x === undefined || k.startsWith('__')) continue;
    if (k === 'attrs') {
      const a = {};
      let any = false;
      for (const ak of Object.keys(x).sort()) {
        if (x[ak] === null || x[ak] === undefined) continue;
        a[ak] = normal(x[ak]);
        any = true;
      }
      if (any) out.attrs = a;
      continue;
    }
    if ((k === 'marks' || k === 'content') && Array.isArray(x) && !x.length) continue;
    out[k] = normal(x);
  }
  return out;
}

/** Marks of a text node as one comparable string. */
export const marksKey = node => (node.marks?.length ? canon(node.marks) : '');

const INLINE_TEXT = {
  hardBreak: () => '\n',
  mathInline: n => n.attrs?.latex || '',
  image: n => n.attrs?.alt || '',
};
const BLOCKS_WITH_LINES = new Set(['doc', 'bulletList', 'orderedList', 'taskList', 'listItem', 'taskItem',
  'blockquote', 'table', 'tableRow', 'sandbox']);

/** All the readable text inside a node. Blocks are separated by new lines. */
export function textOf(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (INLINE_TEXT[node.type]) return INLINE_TEXT[node.type](node);
  if (node.type === 'mathBlock') return node.attrs?.latex || '';
  const kids = node.content || [];
  const sep = BLOCKS_WITH_LINES.has(node.type) ? '\n' : node.type === 'tableCell' || node.type === 'tableHeader' ? ' ' : '';
  return kids.map(textOf).join(sep);
}

/** Headings at any depth, in order: [{ level, text, index }] (index = order among headings). */
export function headingsOf(doc) {
  const out = [];
  const walk = n => {
    if (n.type === 'heading') out.push({ level: n.attrs?.level || 1, text: textOf(n).trim(), index: out.length });
    else for (const k of n.content || []) walk(k);
  };
  if (doc) walk(doc);
  return out.filter(h => h.text);
}

export function wordCount(text) {
  const m = String(text || '').match(/[\p{L}\p{N}][\p{L}\p{N}'’_-]*/gu);
  return m ? m.length : 0;
}

/** First readable line(s), for the doc list. */
export function snippetOf(doc, max = 180) {
  const blocks = (doc?.content || []).map(textOf).map(s => s.replace(/\s+/g, ' ').trim()).filter(Boolean);
  return blocks.join(' · ').slice(0, max);
}

/** Every image src in a doc (to know which uploaded files it uses). */
export function imagesOf(doc) {
  const out = [];
  const walk = n => {
    if (n.type === 'image' && n.attrs?.src) out.push(n.attrs.src);
    for (const k of n.content || []) walk(k);
  };
  if (doc) walk(doc);
  return out;
}

export const emptyDoc = () => ({ type: 'doc', content: [{ type: 'paragraph' }] });

/** Drop containers left empty (a merge that dropped every item of a
    list, say) so the result still fits the schema. */
const NEEDS_CONTENT = new Set(['bulletList', 'orderedList', 'taskList', 'blockquote', 'table', 'tableRow',
  'listItem', 'taskItem', 'sandbox']);
export function tidy(node) {
  if (!node.content) return node;
  const content = node.content.map(tidy).filter(k => !(NEEDS_CONTENT.has(k.type) && !(k.content && k.content.length)));
  if (node.type === 'doc' && !content.length) content.push({ type: 'paragraph' });
  if ((node.type === 'listItem' || node.type === 'taskItem') && content[0]?.type !== 'paragraph') {
    content.unshift({ type: 'paragraph' });
  }
  return { ...node, content };
}
